import { fetchOneliveEnvConfig } from './api';

export type JoinFieldRule = {
  visible: boolean;
  required: boolean;
};

export type OneliveEnvConfig = {
  cf_use_member?: string;
  cf_use_recommend?: string;
  cf_use_join?: string;
  cf_join_url?: string;
  cf_result_txt?: string;
  cf_stipulation?: string;
  cf_privacy?: string;
  cf_use_id_repeat?: string;
  cf_use_nick_repeat?: string;
  join_fields?: Record<string, JoinFieldRule>;
};

const DEFAULT_JOIN_FIELDS: Record<string, JoinFieldRule> = {
  name: { visible: true, required: true },
  nickname: { visible: true, required: true },
  real_name: { visible: true, required: true },
  mobile: { visible: true, required: true },
  gender: { visible: true, required: false },
  email: { visible: false, required: false },
  password_confirm: { visible: true, required: true },
  recommend_code: { visible: false, required: false },
};

export function readOneliveEnvConfig(): OneliveEnvConfig | null {
  const fromWindow = (window as { G7Config?: { modules?: Record<string, unknown> } }).G7Config?.modules?.['onelive-admin'];
  if (fromWindow && typeof fromWindow === 'object') {
    return fromWindow as OneliveEnvConfig;
  }
  return null;
}

export async function loadOneliveEnvConfig(): Promise<OneliveEnvConfig> {
  const cached = readOneliveEnvConfig();
  if (cached) return cached;
  const fetched = await fetchOneliveEnvConfig();
  return (fetched as OneliveEnvConfig) ?? {};
}

export function isEnvEnabled(value?: string): boolean {
  return String(value ?? '0') === '1';
}

export function joinFieldRules(config: OneliveEnvConfig | null | undefined): Record<string, JoinFieldRule> {
  return {
    ...DEFAULT_JOIN_FIELDS,
    ...(config?.join_fields ?? {}),
  };
}

export function isRecommendRequired(config: OneliveEnvConfig | null | undefined): boolean {
  return isEnvEnabled(config?.cf_use_recommend);
}

export function isMemberRegistrationEnabled(config: OneliveEnvConfig | null | undefined): boolean {
  return config?.cf_use_member == null || isEnvEnabled(config.cf_use_member);
}

export function requiresJoinAgreement(config: OneliveEnvConfig | null | undefined): boolean {
  return config?.cf_use_join == null || isEnvEnabled(config.cf_use_join);
}

export function requiresPasswordConfirm(config: OneliveEnvConfig | null | undefined): boolean {
  const fields = joinFieldRules(config);
  return fields.password_confirm?.visible !== false;
}

export function requiresUsernameDuplicateCheck(config: OneliveEnvConfig | null | undefined): boolean {
  return config?.cf_use_id_repeat == null || isEnvEnabled(config.cf_use_id_repeat);
}

export function requiresNicknameDuplicateCheck(config: OneliveEnvConfig | null | undefined): boolean {
  return config?.cf_use_nick_repeat == null || isEnvEnabled(config.cf_use_nick_repeat);
}

export function registerCompleteMessage(config: OneliveEnvConfig | null | undefined): string {
  const custom = config?.cf_result_txt?.trim();
  return custom || '회원가입이 완료되었습니다.';
}

export function registerCompleteRedirect(config: OneliveEnvConfig | null | undefined): string {
  return config?.cf_join_url?.trim() || '';
}
